import { Badge, Button, Card, CardBody, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useServicesPage } from '@/hooks/useServicesPage';
import { getMediaUrl } from '@/utils/imageUrl';

const MICEServices = ({ sectionData: propSectionData }) => {
  const { section: fetchedSection, loading, error } = useServicesPage('groups_mice');
  const section = propSectionData || fetchedSection;

  const bgColor = section?.background_color || 'light';
  const bgEffect = section?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }

  if (loading && !propSectionData) {
    return (
      <section id="groups-mice" className={`py-5 py-lg-6 ${bgClass} services-section mice-services-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }

  if (error || !section || !section.is_active) {
    return null;
  }

  const groupServices = section.items || [];
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  const defaultButton = {
    label: 'Send your inquiry',
    url: '/contact',
    color: 'primary',
    variant: 'solid',
    IconComponent: null
  };
  const sectionButton = section.content?.button || defaultButton;

  return (
    <section id="groups-mice" className={`py-5 py-lg-6 ${bgClass} services-section mice-services-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        <Row className="mb-4">
          <Col>
            {section.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary">
                {section.subtitle}
              </Badge>
            )}
            {section.title && (
              <h2 className="sec-title mb-3">{section.title}</h2>
            )}
          </Col>
        </Row>
        <Row className="mb-4 align-items-center">
          <Col lg={9}>
            {section.intro_text && (
              <p className="lead intro-text mb-0" dangerouslySetInnerHTML={{ __html: section.intro_text }} />
            )}
          </Col>
          <Col lg={3} className="d-flex justify-content-lg-end mt-4 mt-lg-0">
            <Button
              href={sectionButton.url}
              size="lg"
              className="w-100 w-lg-auto"
              variant={sectionButton.variant === 'outline' ? `outline-${sectionButton.color || 'primary'}` : (sectionButton.color || 'primary')}
            >
              {sectionButton.label}
              {sectionButton.IconComponent && <sectionButton.IconComponent className="ms-2" />}
            </Button>
          </Col>
        </Row>
        <Row className="g-4">
          {groupServices.map((service, idx) => {
            const Icon = service.IconComponent;
            const backgroundImage = getMediaUrl(service.background_image);
            const bullets = service.content?.bullets || [];
            
            return (
              <Col key={service.id || idx} md={6} lg={3}>
                <Card className="h-100 border-0 shadow-sm card-hover-shadow service-item-card">
                  {backgroundImage ? (
                    <div 
                      className="service-item-header"
                      style={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '4/3',
                        overflow: 'hidden',
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '0.5rem 0.5rem 0 0'
                      }}
                    >
                      {/* Gradient overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%)',
                          zIndex: 1
                        }}
                      />
                      {/* Icon overlay */}
                      {Icon && (
                        <div 
                          className="icon-lg text-primary rounded-circle flex-centered"
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 2,
                            width: '80px',
                            height: '80px',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          <Icon size={32} />
                        </div>
                      )}
                    </div>
                  ) : (
                    Icon && (
                      <div className="icon-lg bg-white border rounded-circle text-primary flex-centered mb-3" style={{ margin: '1.5rem 1.5rem 0 1.5rem' }}>
                        <Icon className="fs-4" />
                      </div>
                    )
                  )}
                  <CardBody className="p-4 d-flex flex-column">
                    {service.title && <h5 className="mb-2 fw-bold">{service.title}</h5>}
                    {service.description && <p style={{ color: '#495057', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: service.description }} />}
                    {bullets.length > 0 && (
                      <ul className="small ps-3 mb-0" style={{ color: '#495057', lineHeight: '1.6' }}>
                        {bullets.map((item, bulletIdx) => (
                          <li key={bulletIdx}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </section>
  );
};

export default MICEServices;

